import { forwardRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export const Contact = forwardRef(({ plan }, ref) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (plan) setForm((f) => ({ ...f, message: `I'm interested in the ${plan}.` }));
  }, [plan]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, plan: plan || null });
      setDone(true);
      toast.success("Received. Our team will reach out within one business day.");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white rounded-xl border border-[#D8E6F1] px-4 py-3.5 text-[#0C2A40] placeholder:text-[#93A5B5] focus:border-[#2880B8] focus:ring-2 focus:ring-[#2880B8]/20 focus:outline-none transition-all duration-300 text-sm";

  return (
    <section id="contact" ref={ref} data-testid="contact" className="relative z-10 bg-white py-16 md:py-24 border-t border-[#D8E6F1]">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-14">
        <div>
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#2880B8] mb-5">/ Talk to Astra</p>
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tighter text-[#0C2A40] leading-[0.95]">
            Talk with the team <span className="font-extrabold text-[#2880B8]">behind Astra.</span>
          </h2>
          <p className="mt-7 text-[#51667A] leading-relaxed max-w-md text-lg">
            Ask about platform fit, billing support, onboarding, pricing, or how Astra connects EHR, PMS, RCM and HRMS into one workspace.
          </p>

          <div className="mt-9 space-y-4 text-[#51667A]">
            <a href="mailto:Sales@astraaios.com" className="flex items-center gap-3 hover:text-[#2880B8] transition-colors"><Mail size={17} className="text-[#2880B8]" /> Sales@astraaios.com</a>
            <a href="tel:3214411398" className="flex items-center gap-3 hover:text-[#2880B8] transition-colors"><Phone size={17} className="text-[#2880B8]" /> 321-441-1398</a>
          </div>

          <div className="mt-9 space-y-3 font-mono text-[11px] tracking-[0.15em] uppercase text-[#51667A]">
            <p className="text-[#2880B8]">Live in days, not months</p>
            <p>AES-256 encrypted intake</p>
            <p>HIPAA-compliant onboarding</p>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onSubmit={submit}
          className="bg-[#F2F8FC] rounded-2xl border border-[#D8E6F1] p-6 md:p-8 space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input data-testid="contact-name" required placeholder="Full name" value={form.name} onChange={update("name")} className={inputCls} />
            <input data-testid="contact-email" required type="email" placeholder="Work email" value={form.email} onChange={update("email")} className={inputCls} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input data-testid="contact-company" placeholder="Practice / company" value={form.company} onChange={update("company")} className={inputCls} />
            <input data-testid="contact-phone" placeholder="Phone" value={form.phone} onChange={update("phone")} className={inputCls} />
          </div>
          {plan && (
            <div className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#2880B8] py-1">
              Selected: {plan}
            </div>
          )}
          <textarea data-testid="contact-message" rows={4} placeholder="Tell us about your practice..." value={form.message} onChange={update("message")} className={inputCls} />
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={loading || done}
            className="w-full py-4 rounded-full bg-[#2880B8] text-white font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-[#1F6EA1] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : done ? "Request Received" : (<>Talk to Astra <ArrowRight size={16} /></>)}
          </button>
        </motion.form>
      </div>
    </section>
  );
});

Contact.displayName = "Contact";
